// ContractFormDrawer.jsx
// Form drawer for creating and editing employment contracts

import React, { useState, useEffect, useRef } from "react";
import RightDrawer from "../../shared/components/RightDrawer";
import axiosInstance from "../../../../common/axiosInstance";

const DEFAULT_TYPES = ["Permanent", "Fixed-Term", "Internship", "Probationary", "Contractor"];
const DEFAULT_EMP_TYPES = ["Full-Time", "Part-Time", "Internship", "Consultant", "Temporary"];
const DEFAULT_DEPTS = ["Engineering", "Design & UI/UX", "Human Resources", "Management", "QA", "DevOps", "Operations", "Sales & Marketing"];
const DEFAULT_SCHEDULES = ["General Shift (09:00 - 18:00)", "Morning Shift (07:00 - 16:00)", "Evening Shift (14:00 - 23:00)", "Night Shift (22:00 - 07:00)", "Flexible Working Hours"];

const ContractFormDrawer = ({
  isOpen = false,
  onClose,
  contract = null,
  initialData = null,
  onSave,
  onSubmit,
  options = {},
  employees = [],
}) => {
  const targetContract = contract || initialData;
  const isEditing = Boolean(targetContract);
  const handleFinalSubmit = onSubmit || onSave;

  const typeOptions = Array.isArray(options.types) && options.types.length > 0 ? options.types.filter(t => t !== "All") : DEFAULT_TYPES;
  const empTypeOptions = Array.isArray(options.employmentTypes) && options.employmentTypes.length > 0 ? options.employmentTypes : DEFAULT_EMP_TYPES;
  const deptOptions = Array.isArray(options.departments) && options.departments.length > 0 ? options.departments.filter(d => d !== "All") : DEFAULT_DEPTS;
  const scheduleOptions = Array.isArray(options.workSchedules) && options.workSchedules.length > 0 ? options.workSchedules : DEFAULT_SCHEDULES;

  const [formData, setFormData] = useState({
    employee_name: "",
    employee_id: "",
    contract_type: "Permanent",
    start_date: "",
    end_date: "",
    duration: "",
    employment_type: "Full-Time",
    department: "Engineering",
    work_schedule: "",
    probation_period: "6 Months",
    renewal_notice_period: "30 Days",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (targetContract) {
      setFormData({
        employee_name: targetContract.employee_name || targetContract.full_name || "",
        employee_id: targetContract.employee_id || targetContract.employee_code || "",
        contract_type: targetContract.contract_type || "Permanent",
        start_date: targetContract.start_date || "",
        end_date: targetContract.end_date || "",
        duration: targetContract.duration || "",
        employment_type: targetContract.employment_type || targetContract.employment_type_name || "Full-Time",
        department: targetContract.department || targetContract.department_name || "Engineering",
        work_schedule: targetContract.work_schedule || targetContract.work_schedule_name || (scheduleOptions[0] || ""),
        probation_period: targetContract.probation_period || "6 Months",
        renewal_notice_period: targetContract.renewal_notice_period || "30 Days",
      });
    } else {
      setFormData({
        employee_name: "",
        employee_id: "",
        contract_type: "Permanent",
        start_date: new Date().toISOString().split("T")[0],
        end_date: "",
        duration: "",
        employment_type: "Full-Time",
        department: "Engineering",
        work_schedule: scheduleOptions[0] || "",
        probation_period: "6 Months",
        renewal_notice_period: "30 Days",
      });
    }
    setErrors({});
  }, [targetContract, isOpen, scheduleOptions]);

  // Employee Autocomplete State
  const [employeesList, setEmployeesList] = useState([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [showIdSuggestions, setShowIdSuggestions] = useState(false);
  const nameWrapperRef = useRef(null);
  const idWrapperRef = useRef(null);

  useEffect(() => {
    if (employees && employees.length > 0) {
      setEmployeesList(employees);
    } else if (isOpen && employeesList.length === 0) {
      axiosInstance.get("/employees")
        .then(res => {
          if (res.data?.data) {
            setEmployeesList(res.data.data);
          } else if (Array.isArray(res.data)) {
            setEmployeesList(res.data);
          }
        })
        .catch(err => console.error("Failed to load employees for autocomplete", err));
    }
  }, [isOpen, employees, employeesList.length]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (nameWrapperRef.current && !nameWrapperRef.current.contains(e.target)) {
        setShowNameSuggestions(false);
      }
      if (idWrapperRef.current && !idWrapperRef.current.contains(e.target)) {
        setShowIdSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectEmployee = (emp) => {
    setFormData((prev) => ({
      ...prev,
      employee_name: emp.full_name || `${emp.first_name || ""} ${emp.last_name || ""}`.trim(),
      employee_id: emp.employee_id || emp.employee_code || "",
      department: emp.department_name || prev.department,
      employment_type: emp.employment_type_name || prev.employment_type,
      contract_type: emp.contract_type || prev.contract_type,
      work_schedule: emp.work_schedule_name || prev.work_schedule,
    }));
    setShowNameSuggestions(false);
    setShowIdSuggestions(false);
    setErrors((prev) => ({ ...prev, employee_name: null, employee_id: null }));
  };

  const filteredByName = employeesList.filter((emp) => {
    const name = emp.full_name || `${emp.first_name || ""} ${emp.last_name || ""}`.trim();
    return name.toLowerCase().includes(formData.employee_name.toLowerCase());
  });

  const filteredById = employeesList.filter((emp) => {
    const code = emp.employee_id || emp.employee_code || "";
    return code.toLowerCase().includes(formData.employee_id.toLowerCase());
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!formData.employee_name.trim()) errs.employee_name = "Employee name is required";
    if (!formData.contract_type) errs.contract_type = "Contract type is required";
    if (!formData.start_date) errs.start_date = "Start date is required";

    if (formData.contract_type !== "Permanent" && !formData.end_date) {
      errs.end_date = "End date is required for fixed-term contracts";
    }

    if (formData.start_date && formData.end_date) {
      if (new Date(formData.end_date) <= new Date(formData.start_date)) {
        errs.end_date = "End date must be after start date";
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      handleFinalSubmit?.(formData);
    }
  };

  return (
    <RightDrawer
      open={isOpen}
      onClose={onClose}
      title={isEditing ? "Edit Employment Contract" : "New Contract"}
      description={
        isEditing
          ? "Update details of existing employment contract."
          : "Create a new employment contract for an employee."
      }
      width="520px"
      footer={
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="rounded-xl bg-[#147db5] px-5 py-2 text-xs font-semibold text-white hover:bg-[#116d9d] shadow-sm transition"
          >
            {isEditing ? "Update Contract" : "Create Contract"}
          </button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs">
        {/* Employee Name */}
        <div ref={nameWrapperRef} className="relative">
          <label className="block font-semibold text-slate-700 mb-1">
            Employee Name <span className="text-rose-500">*</span>
          </label>
          <input
            type="text"
            name="employee_name"
            value={formData.employee_name}
            onChange={handleChange}
            onFocus={() => setShowNameSuggestions(true)}
            placeholder="e.g. Sarah Jenkins"
            autoComplete="off"
            className={`w-full rounded-xl border px-3.5 py-2.5 bg-white text-slate-800 transition focus:outline-none ${
              errors.employee_name ? "border-rose-400 focus:ring-1 focus:ring-rose-400" : "border-slate-200 focus:border-[#147db5]"
            }`}
          />
          {errors.employee_name && (
            <p className="mt-1 text-[11px] text-rose-500">{errors.employee_name}</p>
          )}

          {showNameSuggestions && formData.employee_name && filteredByName.length > 0 && (
            <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg">
              {filteredByName.map((emp) => (
                <li
                  key={emp.uuid}
                  onClick={() => handleSelectEmployee(emp)}
                  className="cursor-pointer px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                >
                  <div className="font-semibold">{emp.full_name}</div>
                  <div className="text-[11px] text-slate-400">{emp.employee_code}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Employee ID */}
        <div ref={idWrapperRef} className="relative">
          <label className="block font-semibold text-slate-700 mb-1">
            Employee ID
          </label>
          <input
            type="text"
            name="employee_id"
            value={formData.employee_id}
            onChange={handleChange}
            onFocus={() => setShowIdSuggestions(true)}
            placeholder="e.g. EMP-1001"
            autoComplete="off"
            className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 bg-white text-slate-800 focus:border-[#147db5] focus:outline-none"
          />

          {showIdSuggestions && formData.employee_id && filteredById.length > 0 && (
            <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg">
              {filteredById.map((emp) => (
                <li
                  key={emp.uuid}
                  onClick={() => handleSelectEmployee(emp)}
                  className="cursor-pointer px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                >
                  <div className="font-semibold">{emp.employee_code}</div>
                  <div className="text-[11px] text-slate-400">{emp.full_name}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Contract Type & Employment Type */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold text-slate-700 mb-1">
              Contract Type <span className="text-rose-500">*</span>
            </label>
            <select
              name="contract_type"
              value={formData.contract_type}
              onChange={handleChange}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 bg-white text-slate-800 focus:border-[#147db5] focus:outline-none"
            >
              {typeOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 mb-1">
              Employment Type
            </label>
            <select
              name="employment_type"
              value={formData.employment_type}
              onChange={handleChange}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 bg-white text-slate-800 focus:border-[#147db5] focus:outline-none"
            >
              {empTypeOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Start Date & End Date */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold text-slate-700 mb-1">
              Start Date <span className="text-rose-500">*</span>
            </label>
            <input
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              className={`w-full rounded-xl border px-3 py-2.5 bg-white text-slate-800 focus:outline-none ${
                errors.start_date ? "border-rose-400" : "border-slate-200 focus:border-[#147db5]"
              }`}
            />
            {errors.start_date && (
              <p className="mt-1 text-[11px] text-rose-500">{errors.start_date}</p>
            )}
          </div>

          <div>
            <label className="block font-semibold text-slate-700 mb-1">
              End Date {formData.contract_type !== "Permanent" && <span className="text-rose-500">*</span>}
            </label>
            <input
              type="date"
              name="end_date"
              value={formData.end_date}
              onChange={handleChange}
              className={`w-full rounded-xl border px-3 py-2.5 bg-white text-slate-800 focus:outline-none ${errors.end_date ? "border-rose-400" : "border-slate-200 focus:border-[#147db5]"}`}
            />
            {errors.end_date && (
              <p className="mt-1 text-[11px] text-rose-500">{errors.end_date}</p>
            )}
          </div>
        </div>

        {/* Department & Work Schedule */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold text-slate-700 mb-1">
              Department
            </label>
            <select
              name="department"
              value={formData.department}
              onChange={handleChange}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 bg-white text-slate-800 focus:border-[#147db5] focus:outline-none"
            >
              {deptOptions.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 mb-1">
              Work Schedule
            </label>
            <select
              name="work_schedule"
              value={formData.work_schedule}
              onChange={handleChange}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 bg-white text-slate-800 focus:border-[#147db5] focus:outline-none"
            >
              {scheduleOptions.map((ws) => (
                <option key={ws} value={ws}>
                  {ws}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Probation Period & Renewal Notice Period */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold text-slate-700 mb-1">
              Probation Period
            </label>
            <input
              type="text"
              name="probation_period"
              value={formData.probation_period}
              onChange={handleChange}
              placeholder="e.g. 6 Months"
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 bg-white text-slate-800 focus:border-[#147db5] focus:outline-none"
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 mb-1">
              Renewal Notice Period
            </label>
            <input
              type="text"
              name="renewal_notice_period"
              value={formData.renewal_notice_period}
              onChange={handleChange}
              placeholder="e.g. 30 Days"
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 bg-white text-slate-800 focus:border-[#147db5] focus:outline-none"
            />
          </div>
        </div>
      </form>
    </RightDrawer>
  );
};

export default ContractFormDrawer;
